import { CopilotCartContext, OffreMensuelle, ProduitPaiement } from '@app-types/copilot-context';
import { PRICE_TYPE } from '@app-types/price';
import { CartType } from '@app-types/product';

const EMPTY_CONTEXT: CopilotCartContext = {
    idPanier: '',
    totaux: {
        mensuel: '0,00 €',
        immediat: '0,00 €',
        premiere_facture: '0,00 €',
    },
    offres_mensuelles: [],
    frais_uniques: [],
    paiements_immediats: [],
    paiements_differes: [],
    frais_livraison: { disponibles: false, modes: [], address_configured: false },
    options: { portabilite: false },
};

const formatPrice = (amount: number): string => `${amount.toFixed(2).replace('.', ',')} €`;

const buildMonthlyOffers = (cart: CartType): OffreMensuelle[] => {
    const products = cart.product ? [cart.product, ...cart.options] : cart.options;

    return products.map((product) => {
        const offer: OffreMensuelle = {
            name: product.name,
            gencode: product.gencode,
            price: formatPrice(product.prices?.final ?? product.price ?? 0),
        };

        if (product.prices && product.prices.forever !== product.prices.final) {
            offer.after_12_months = formatPrice(product.prices.forever);
        }

        if ('commercialRange' in product && product.commercialRange) {
            offer.gamme = product.commercialRange;
        }
        if ('typeId' in product) {
            offer.type_forfait = product.typeId;
        }

        return offer;
    });
};

const buildPaymentsByType = (cart: CartType, priceType: PRICE_TYPE): ProduitPaiement[] => {
    const payments: ProduitPaiement[] = [];

    if (cart.sim?.priceType === priceType) {
        payments.push({ name: 'Carte SIM', gencode: cart.sim.gencode, prix_original: formatPrice(cart.sim.price) });
    }
    if (cart.mobileFees?.prices.type === priceType) {
        payments.push({
            name: cart.mobileFees.name,
            gencode: cart.mobileFees.gencode,
            prix_original: formatPrice(cart.mobileFees.prices.initial),
        });
    }

    return payments;
};

const sumByPriceType = (cart: CartType, priceType: PRICE_TYPE): number => {
    let total = 0;

    if (cart.sim?.priceType === priceType) total += cart.sim.price;
    if (cart.mobileFees?.prices.type === priceType) total += cart.mobileFees.price;
    if (cart.deliveryMode?.priceType === priceType) total += cart.deliveryMode.price;

    return total;
};

export const buildCopilotCartContext = (cart: CartType | null | undefined): CopilotCartContext => {
    if (!cart?.gencode || !cart.product) return structuredClone(EMPTY_CONTEXT);

    try {
        const deliveryMode = cart.deliveryMode;

        return {
            idPanier: String(cart.cartId ?? cart.id ?? cart.tempId),
            totaux: {
                mensuel: formatPrice(cart.total),
                immediat: formatPrice(sumByPriceType(cart, PRICE_TYPE.TODAY)),
                premiere_facture: formatPrice(sumByPriceType(cart, PRICE_TYPE.DEFERRED)),
            },
            offres_mensuelles: buildMonthlyOffers(cart),
            frais_uniques: cart.mobileFees
                ? [
                      {
                          name: cart.mobileFees.name,
                          gencode: cart.mobileFees.gencode,
                          price: formatPrice(cart.mobileFees.price),
                      },
                  ]
                : [],
            paiements_immediats: buildPaymentsByType(cart, PRICE_TYPE.TODAY),
            paiements_differes: buildPaymentsByType(cart, PRICE_TYPE.DEFERRED),
            frais_livraison: {
                disponibles: Boolean(deliveryMode),
                modes: deliveryMode
                    ? [
                          {
                              gencode: deliveryMode.gencode,
                              nom: deliveryMode.label,
                              prix: formatPrice(deliveryMode.price),
                              typePrix: deliveryMode.priceType,
                          },
                      ]
                    : [],
                address_configured: Boolean(cart.address),
            },
            options: {
                portabilite: cart.portability?.keepNumber === true,
            },
        };
    } catch {
        return structuredClone(EMPTY_CONTEXT);
    }
};
